import {
  Bill,
  Receivable,
  BankAccount,
  JournalEntry,
  ChartOfAccount,
  Distribution,
  Budget,
  CAMData,
  BankTransaction
} from '@repo/shared-types';
import { ReconciliationTransaction, ReconciliationMatch } from '@repo/shared-types/accounting/reconciliation';
import { RecurringBill } from '@repo/shared-types/accounting/recurringBills';
import type { PaymentPlan } from '@repo/shared-types/accounting/paymentPlans';
import type { PurchaseOrder } from '@repo/shared-types/accounting/purchaseOrders';
import type { ThreeWayMatch } from '@repo/shared-types/accounting/purchaseOrders';
import type { ManagementFeeSweepPostBody, ManagementFeeSweepReport } from '@repo/shared-types/accounting/managementFees';
import type {
  ManagementFeeSettlementPostBody,
  ManagementFeeSettlementReport,
} from '@repo/shared-types/accounting/managementFeeSettlements';
import type { OwnerDistributionPostBody, OwnerDistributionRunReport } from '@repo/shared-types/accounting/ownerDistributions';
import { apiTransport as router } from './transport';
import { getContext } from './core';
import { fileToBase64 } from '@/utils/fileUtils';

export type StatementBatchStatusApi =
  | 'PENDING'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'PARTIAL_SUCCESS'
  | 'FAILED';

export type StatementBatchFailureRowApi = {
  propertyId: string;
  ownerId?: string;
  reason: string;
  message: string;
};

export type StatementBatchSummaryJsonApi = {
  totalCount?: number;
  total?: number;
  successCount?: number;
  failureCount?: number;
  cachedSuccessCount?: number;
  failures?: StatementBatchFailureRowApi[];
};

export type StatementBatchRow = {
  id: string;
  operatingOrganizationId: string;
  yyyyMm: string;
  status: StatementBatchStatusApi;
  summaryJson: unknown;
  triggeredByUserId: string | null;
  startedAt: string;
  finishedAt: string | null;
};

export type StatementBatchExecuteResultApi = {
  batchId: string;
  status: StatementBatchStatusApi;
  summary: StatementBatchSummaryJsonApi;
};

// Bank-account / bank-transaction PATCH: backend returns 501 until edits persist. COA DELETE is implemented with 409 guardrails (journal lines, child accounts, ledger balances, SEED). Journal PATCH/DELETE and POST bank-transactions/:id/unmatch are implemented.

export const accountingApi = {
  bills: {
    list: async (filters?: Record<string, string>) => {
      const context = getContext();
      return router.get<Bill[]>('/accounting/bills', context, Object.keys(filters ?? {}).length ? filters : undefined);
    },
    get: async (id: string) => {
      const context = getContext();
      return router.get<Bill>(`/accounting/bills/${id}`, context);
    },
    create: async (bill: Omit<Bill, 'id' | 'paidDate' | 'createdAt' | 'updatedAt'>) => {
      const context = getContext();
      return router.post<Bill>('/accounting/bills', bill, context);
    },
    approve: async (id: string) => {
      const context = getContext();
      return router.post<Bill>(`/accounting/bills/${id}/approve`, {}, context);
    },
    pay: async (id: string, bankAccountId: string) => {
      const context = getContext();
      return router.post<Bill>(`/accounting/bills/${id}/pay`, { bankAccountId }, context);
    },
    delete: async (id: string) => {
      const context = getContext();
      return router.delete<{ success: boolean }>(`/accounting/bills/${id}`, context);
    },
    attachments: {
      list: async (billId: string) => router.get<{ id: string; name: string; url?: string }[]>(`/accounting/bills/${billId}/attachments`),
      add: async (billId: string, file: File, description?: string) => {
        const { currentUserId } = getContext();
        const content = await fileToBase64(file);
        const payload = {content, description, name: file.name, type: file.type, size: file.size, entityType: 'Bill', entityId: billId, uploadedBy: currentUserId, folder: 'bill-attachments'};
        return router.post<{ id: string; name: string; entityId?: string }>(`/accounting/bills/${billId}/attachments`, payload);
      },
      delete: async (fileId: string) => {
        const context = getContext();
        return router.delete<{ success: boolean }>(`/accounting/bills/attachments/${fileId}`, context);
      }
    },
    recurring: {
      list: async () => router.get<RecurringBill[]>('/accounting/recurring-bills'),
      get: async (id: string) => router.get<RecurringBill>(`/accounting/recurring-bills/${id}`),
      create: async (bill: Omit<RecurringBill, 'id' | 'createdAt' | 'updatedAt'>) => {
        const context = getContext();
        return router.post<RecurringBill>('/accounting/recurring-bills', bill, context);
      },
      update: async (id: string, updates: Partial<RecurringBill>) => {
        const context = getContext();
        return router.patch<RecurringBill>(`/accounting/recurring-bills/${id}`, updates, context);
      },
      delete: async (id: string) => {
        const context = getContext();
        return router.delete<{ success: boolean }>(`/accounting/recurring-bills/${id}`, context);
      },
      generate: async () => router.post<import('@repo/shared-types/accounting/recurringBills').RecurringBillGenerationResult>('/accounting/recurring-bills/generate', {})
    }
  },
  receivables: {
    list: async (filters?: Record<string, string>) => {
      const context = getContext();
      return router.get<Receivable[]>('/accounting/receivables', context, Object.keys(filters ?? {}).length ? filters : undefined);
    },
    update: async (id: string, updates: Partial<Receivable> & { bankId?: string }) => {
      const context = getContext();
      return router.patch<Receivable>(`/accounting/receivables/${id}`, updates, context);
    },
    checkLateFees: async () => {
      const context = getContext();
      return router.post<{ processed: number }>('/accounting/receivables/check-late-fees', {}, context);
    },
    delete: async (id: string) => {
      const context = getContext();
      return router.delete<{ success: boolean }>(`/accounting/receivables/${id}`, context);
    }
  },
  bankAccounts: {
    list: async () => router.get<BankAccount[]>('/accounting/bank-accounts'),
    update: async (id: string, updates: Partial<BankAccount>) => {
      return router.patch<BankAccount>(`/accounting/bank-accounts/${id}`, updates);
    }
  },
  journalEntries: {
    list: async () => router.get<JournalEntry[]>('/accounting/journal-entries'),
    create: async (entry: Omit<JournalEntry, 'id'>) => {
      const context = getContext();
      return router.post<JournalEntry>('/accounting/journal-entries', entry, context);
    },
    update: async (id: string, updates: Partial<JournalEntry>) => {
      const context = getContext();
      return router.patch<JournalEntry>(`/accounting/journal-entries/${id}`, updates, context);
    },
    delete: async (id: string) => {
      const context = getContext();
      return router.delete<{ success: boolean }>(`/accounting/journal-entries/${id}`, context);
    }
  },
  distributions: {
    list: async () => router.get<Distribution[]>('/accounting/distributions'),
    get: async (id: string) => router.get<Distribution>(`/accounting/distributions/${id}`),
    create: async (dist: Partial<Distribution>) => {
      const context = getContext();
      return router.post<Distribution>('/accounting/distributions', dist, context);
    },
    /** Metadata only (notes, propertyId, propertyName). Status/amount use approve/process. */
    update: async (id: string, updates: Pick<Partial<Distribution>, 'notes' | 'propertyId' | 'propertyName'>) => {
      const context = getContext();
      return router.patch<Distribution>(`/accounting/distributions/${id}`, updates, context);
    },
    approve: async (id: string) => {
      const context = getContext();
      return router.post<Distribution>(`/accounting/distributions/${id}/approve`, {}, context);
    },
    process: async (id: string) => {
      const context = getContext();
      return router.post<Distribution>(`/accounting/distributions/${id}/process`, {}, context);
    },
    acknowledge: async (id: string, notes?: string) => {
      const context = getContext();
      return router.post<Distribution>(`/accounting/distributions/${id}/acknowledge`, { notes }, context);
    }
  },
  coa: {
    list: async () => router.get<ChartOfAccount[]>('/accounting/coa'),
    get: async (id: string) => router.get<ChartOfAccount>(`/accounting/coa/${id}`),
    create: async (data: Partial<ChartOfAccount>) => router.post<ChartOfAccount>('/accounting/coa', data),
    update: async (id: string, updates: Partial<ChartOfAccount>) => router.patch<ChartOfAccount>(`/accounting/coa/${id}`, updates),
    delete: async (id: string) => router.delete<{ success: boolean }>(`/accounting/coa/${id}`)
  },
  bankTransactions: {
    list: async (bankAccountId?: string, filters?: { explainability?: boolean }) => {
      const params = new URLSearchParams();
      if (bankAccountId) {
        params.set('bankAccountId', bankAccountId);
      }
      if (filters?.explainability) {
        params.set('explainability', '1');
      }
      const qs = params.toString();
      const path = qs ? `/accounting/bank-transactions?${qs}` : '/accounting/bank-transactions';
      return router.get<BankTransaction[]>(path);
    },
    update: async (id: string, updates: Partial<BankTransaction>) => router.patch<BankTransaction>(`/accounting/bank-transactions/${id}`, updates),
    import: async (bankAccountId: string, fileContent: string, fileType: 'CSV' | 'OFX') => {
      return router.post<{ success: boolean; transactions: BankTransaction[] }>('/accounting/bank-transactions/import', { bankAccountId, fileContent, fileType });
    },
    match: async (bankTransactionId: string, ledgerEntryId: string) => {
      return router.post<BankTransaction>('/accounting/bank-transactions/match', { bankTransactionId, ledgerEntryId });
    },
    unmatch: async (transactionId: string) => {
      const context = getContext();
      return router.post<BankTransaction>(`/accounting/bank-transactions/${transactionId}/unmatch`, {}, context);
    }
  },
  reconciliation: {
    list: async (bankAccountId?: string) => {
      const path = bankAccountId ? `/accounting/reconciliation?bankAccountId=${bankAccountId}` : '/accounting/reconciliation';
      return router.get<ReconciliationTransaction[]>(path);
    },
    get: async (id: string) => router.get<ReconciliationTransaction>(`/accounting/reconciliation/${id}`),
    create: async (data: Partial<ReconciliationTransaction>) => router.post<ReconciliationTransaction>('/accounting/reconciliation', data),
    update: async (id: string, updates: Partial<ReconciliationTransaction>) => router.patch<ReconciliationTransaction>(`/accounting/reconciliation/${id}`, updates),
    match: async (bankAccountId: string) => {
      const context = getContext();
      return router.post<{ success: boolean; matches: number; matched: ReconciliationMatch[] }>(
        '/accounting/reconciliation/auto-match',
        { bankAccountId },
        context,
      );
    },
    complete: async (bankAccountId: string, period: string) => {
      const context = getContext();
      return router.post<{ success: boolean; period?: string; alreadyClosed?: boolean; closedPeriodId?: string }>(
        '/accounting/reconciliation/complete',
        { bankAccountId, period },
        context,
      );
    }
  },
  cam: {
    list: async () => {
      const context = getContext();
      return router.get<CAMData[]>('/accounting/cam', context);
    },
    create: async (data: Partial<CAMData>) => {
      const context = getContext();
      return router.post<CAMData>('/accounting/cam', data, context);
    },
    update: async (id: string, updates: Partial<CAMData>) => {
      const context = getContext();
      return router.patch<CAMData>(`/accounting/cam/${id}`, updates, context);
    },
    delete: async (id: string) => {
      const context = getContext();
      return router.delete<{ success: boolean }>(`/accounting/cam/${id}`, context);
    },
  },
  budgets: {
    list: async () => router.get<Budget[]>('/accounting/budgets'),
    get: async (id: string) => router.get<Budget>(`/accounting/budgets/${id}`),
    create: async (data: Partial<Budget>) => router.post<Budget>('/accounting/budgets', data),
    update: async (id: string, updates: Partial<Budget>) => router.patch<Budget>(`/accounting/budgets/${id}`, updates)
  },
  managementFeeSweeps: {
    run: async (payload: ManagementFeeSweepPostBody) => {
      const context = getContext();
      return router.post<ManagementFeeSweepReport>('/accounting/management-fee-sweeps', payload, context);
    },
  },
  managementFeeSettlements: {
    run: async (payload: ManagementFeeSettlementPostBody) => {
      const context = getContext();
      return router.post<ManagementFeeSettlementReport>('/accounting/management-fee-settlements', payload, context);
    },
  },
  ownerDistributions: {
    run: async (payload: OwnerDistributionPostBody) => {
      const context = getContext();
      return router.post<OwnerDistributionRunReport>('/accounting/owner-distributions', payload, context);
    },
  },
  statementBatches: {
    list: async (params: { operatingOrganizationId: string; yyyyMm: string }) => {
      const context = getContext();
      return router.get<{ batches: StatementBatchRow[] }>('/accounting/statement-batches', context, {
        operatingOrganizationId: params.operatingOrganizationId,
        yyyyMm: params.yyyyMm,
      });
    },
    create: async (body: { operatingOrganizationId: string; yyyyMm: string }) => {
      const context = getContext();
      return router.post<{ batch: StatementBatchRow }>('/accounting/statement-batches', body, context);
    },
    execute: async (batchId: string) => {
      const context = getContext();
      return router.post<StatementBatchExecuteResultApi>(
        `/accounting/statement-batches/${encodeURIComponent(batchId)}/execute`,
        {},
        context,
      );
    },
  },
  paymentPlans: {
    list: async () => router.get<PaymentPlan[]>('/accounting/payment-plans'),
    get: async (id: string) => router.get<PaymentPlan>(`/accounting/payment-plans/${id}`),
    create: async (plan: Record<string, unknown>) => {
      const context = getContext();
      return router.post<PaymentPlan>('/accounting/payment-plans', plan, context);
    },
    update: async (id: string, updates: Partial<PaymentPlan>) => {
      const context = getContext();
      return router.patch<PaymentPlan>(`/accounting/payment-plans/${id}`, updates, context);
    },
    delete: async (id: string) => {
      const context = getContext();
      return router.delete<{ success: boolean }>(`/accounting/payment-plans/${id}`, context);
    }
  },
  purchaseOrders: {
    list: async () => router.get<PurchaseOrder[]>('/accounting/purchase-orders'),
    get: async (id: string) => router.get<PurchaseOrder>(`/accounting/purchase-orders/${id}`),
    create: async (po: Omit<PurchaseOrder, 'id' | 'createdAt' | 'updatedAt'>) => {
      const context = getContext();
      return router.post<PurchaseOrder>('/accounting/purchase-orders', po, context);
    },
    update: async (id: string, updates: Partial<PurchaseOrder>) => {
      const context = getContext();
      return router.patch<PurchaseOrder>(`/accounting/purchase-orders/${id}`, updates, context);
    },
    delete: async (id: string) => {
      const context = getContext();
      return router.delete<{ success: boolean }>(`/accounting/purchase-orders/${id}`, context);
    }
  },
  threeWayMatches: {
    list: async (filters?: { purchaseOrderId?: string; invoiceId?: string; status?: string }) => {
      const queryString = filters ? '?' + new URLSearchParams(filters as Record<string, string>).toString() : '';
      return router.get<ThreeWayMatch[]>(`/accounting/three-way-matches${queryString}`);
    },
    get: async (id: string) => router.get<ThreeWayMatch>(`/accounting/three-way-matches/${id}`),
    create: async (match: { purchaseOrderId: string; invoiceId: string; receiptId?: string }) => {
      const context = getContext();
      return router.post<ThreeWayMatch>('/accounting/three-way-matches', match, context);
    },
    perform: async (poId: string, invoiceId: string, receiptId?: string) => {
      const context = getContext();
      return router.post<ThreeWayMatch>('/accounting/three-way-matches/match', { poId, invoiceId, receiptId }, context);
    },
    approve: async (id: string) => {
      const context = getContext();
      return router.post<ThreeWayMatch>(`/accounting/three-way-matches/${id}/approve`, {}, context);
    },
    flagDiscrepancy: async (id: string, notes: string) => {
      const context = getContext();
      return router.post<ThreeWayMatch>(`/accounting/three-way-matches/${id}/flag`, { notes }, context);
    }
  },
  tenants: {
    fund: async (
      tenantId: string,
      body: { amountCents: number; paymentMethodId?: string; idempotencyKey?: string },
    ) => {
      const context = getContext();
      return router.post<{
        paymentIntentId: string;
        clientSecret?: string;
        status: string;
        stripePublishableKey?: string;
        stripeConnectAccountId: string;
        propertyId: string;
        operatingOrganizationId: string;
        amountCents: number;
      }>(`/accounting/tenants/${encodeURIComponent(tenantId)}/fund`, body, context);
    },
  },
  periods: {
    listFinancialBlockers: async () => {
      const context = getContext();
      return router.get<Array<{ code: string; severity: 'Critical' | 'High'; message: string; route?: string }>>(
        '/accounting/periods/financial-blockers',
        context,
      );
    },
  },
};

